import useAnalytics from 'common/hooks/useAnalytics'
import { forwardRef, useEffect, useCallback, ReactNode } from 'react'
import { Helmet } from 'react-helmet-async'
import { useTranslation } from 'react-i18next'
import { useLocation } from 'react-router-dom'

// material
import { Box, BoxProps } from '@mui/material'

// utils
import track from 'utils/analytics'
import { getScreenName } from 'utils/screenNameRegistry'

// ----------------------------------------------------------------------

interface PageProps extends BoxProps {
  children: ReactNode
  title?: string
  description?: string
  keywords?: string
  image?: string
}

const KEYWORDS = 'ดูฟาร์ม,DoFarm,จดอาหารกุ้ง,บริหารจัดการฟาร์ม,วัดคุณภาพน้ำ,ราคากุ้งวันนี้,ราคากุ้ง'
const IMAGE = '/static/seo/cover.jpg'

const Page = forwardRef<HTMLDivElement, PageProps>(
  (
    {
      children,
      title,
      description,
      keywords = KEYWORDS,
      image = IMAGE,
      ...other
    },
    ref
  ) => {
    const { t } = useTranslation('common')
    const pageTitle = title ?? t('seo.title')
    const pageDescription = description ?? t('seo.description')
    const { pathname } = useLocation()
    const { trackScreen } = useAnalytics()

    const sendPageViewEvent = useCallback(() => {
      const screen = getScreenName(pathname)
      if (screen) {
        trackScreen(screen.screenName, screen.screenClass)
      } else {
        // Fallback for routes not yet in the registry — keeps prior
        // behavior instead of silently dropping the pageview.
        track.pageview({ page_path: pathname })
      }
    }, [pathname, trackScreen])

    useEffect(() => {
      sendPageViewEvent()
    }, [sendPageViewEvent])

    return (
      <Box ref={ref} {...other}>
        <Helmet>
          <meta charSet="utf-8" />
          <title>{pageTitle}</title>
          <meta name="keywords" content={keywords} />
          <meta name="description" content={pageDescription} />
          <meta property="og:title" content={pageTitle} />
          <meta property="og:image" content={image} />
        </Helmet>
        {children}
      </Box>
    )
  }
)

export default Page
